import React, { useState, useEffect } from 'react';
import { 
  Alert, 
  Button, 
  Form, 
  Spinner, 
  Badge,
  InputGroup
} from 'react-bootstrap';
import { db1 } from '../firebase/config';
import { doc, updateDoc, arrayUnion, serverTimestamp, onSnapshot, setDoc } from 'firebase/firestore';

const Contact = () => {
  const [isArabic, setIsArabic] = useState(true);
  const [conversation, setConversation] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatToken, setChatToken] = useState(null);
  const [chatLoading, setChatLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [inquiryUrl, setInquiryUrl] = useState('');
  const [firstMessageSent, setFirstMessageSent] = useState(false);

  const translations = {
    'Al-Majd Media': 'الإعلامي المجد',
    'Official media platform of Al-Majd Initiative': 'المنصة الإعلامية الرسمية لمبادرة المجد',
    'Contact Us': 'اتصل بنا',
    'Live Support': 'الدعم المباشر',
    'Start a conversation with our support team': 'ابدأ محادثة مع فريق الدعم لدينا',
    'Your Message': 'رسالتك',
    'Sending...': 'جاري الإرسال...',
    'Send': 'إرسال',
    'Empty Message': 'لا يمكن أن تكون الرسالة فارغة',
    'Message Sent': 'تم إرسال الرسالة بنجاح!',
    'Error Sending': 'خطأ في إرسال الرسالة',
    'No messages yet. Start the conversation!': 'لا توجد رسائل بعد. ابدأ المحادثة!',
    'You': 'أنت',
    'Support Team': 'فريق الدعم',
    'Type your message here...': 'اكتب رسالتك هنا...',
    'Connecting to support...': 'جاري الاتصال بالدعم...',
    'Chat Connected': 'تم الاتصال بالدعم',
    'Chat Error': 'خطأ في الاتصال',
    'Your Inquiry URL': 'رابط الاستفسار الخاص بك',
    'Copy URL': 'نسخ الرابط',
    'URL Copied': 'تم نسخ الرابط',
    'Share this URL with our support team for follow-up': 'شارك هذا الرابط مع فريق الدعم للمتابعة',
    'Inquiry Created': 'تم إنشاء الاستفسار بنجاح',
    'Truth and credibility are our commitment': 'الحق والمصداقية التزامنا',
    'Start by sending your first message to get your inquiry URL': 'ابدأ بإرسال رسالتك الأولى للحصول على رابط الاستفسار'
  };

  const translateText = (text) => isArabic ? (translations[text] || text) : text;

  const toggleLanguage = () => setIsArabic(!isArabic);

  // Initialize chat session
  useEffect(() => {
    const initializeChat = async () => {
      try {
        // Generate unique token for this inquiry
        const token = `inquiry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setChatToken(token);
        
        // Create initial inquiry document in Firebase (without URL yet)
        const inquiryRef = doc(db1, 'support_inquiries', token);
        await setDoc(inquiryRef, {
          createdAt: serverTimestamp(),
          status: 'initializing',
          language: isArabic ? 'ar' : 'en',
          clientInfo: {
            userAgent: navigator.userAgent,
            timestamp: new Date()
          },
          messages: {
            conversation: [],
            lastUpdated: serverTimestamp()
          },
          firstMessageSent: false,
          inquiryUrl: '' // Will be set after first message
        });

        setupChatListener(token);

      } catch (error) {
        console.error('Error initializing chat:', error);
        setNotification({
          show: true,
          type: 'danger',
          message: translateText('Chat Error')
        });
        setChatLoading(false);
      }
    };

    initializeChat();
  }, []);

  const setupChatListener = (token) => {
    const inquiryRef = doc(db1, 'support_inquiries', token);
    
    const unsubscribe = onSnapshot(inquiryRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const messages = data.messages?.conversation?.map(msg => ({
          ...msg,
          timestamp: msg.timestamp?.toDate() || new Date()
        })) || [];
        
        setConversation(messages);
        setChatLoading(false);

        // Check if first message was sent and URL is available
        if (data.firstMessageSent && data.inquiryUrl) {
          setFirstMessageSent(true);
          setInquiryUrl(data.inquiryUrl);
        }
      }
    }, (error) => {
      console.error('Error listening to chat:', error);
      setChatLoading(false);
    });

    return unsubscribe;
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSendingMessage(true);

    if (!newMessage.trim()) {
      setNotification({
        show: true,
        type: 'danger',
        message: translateText('Empty Message')
      });
      setSendingMessage(false);
      return;
    }

    try {
      const messageData = {
        sender: 'client',
        message: newMessage.trim(),
        timestamp: new Date(),
        isFirstMessage: conversation.length === 0 // Mark if this is the first message
      };

      const inquiryRef = doc(db1, 'support_inquiries', chatToken);

      // If this is the first message, generate and store the URL
      if (conversation.length === 0) {
        const inquiryUrl = `${window.location.origin}/inquiry/${chatToken}`;
        
        await updateDoc(inquiryRef, {
          'messages.conversation': arrayUnion(messageData),
          'messages.lastUpdated': serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: 'waiting_support',
          firstMessageSent: true,
          inquiryUrl: inquiryUrl,
          // Store token in a separate collection for easy lookup
          token: chatToken
        });

        // Also store in tokens collection for security
        await setDoc(doc(db1, 'inquiry_tokens', chatToken), {
          inquiryId: chatToken,
          createdAt: serverTimestamp(),
          status: 'active',
          url: inquiryUrl
        });

        setInquiryUrl(inquiryUrl);
        setFirstMessageSent(true);

      } else {
        // For subsequent messages, just update conversation
        await updateDoc(inquiryRef, {
          'messages.conversation': arrayUnion(messageData),
          'messages.lastUpdated': serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: 'waiting_support'
        });
      }

      setNewMessage('');
      setNotification({
        show: true,
        type: 'success',
        message: translateText('Message Sent')
      });

    } catch (err) {
      setNotification({
        show: true,
        type: 'danger',
        message: `${translateText('Error Sending')}: ${err.message}`
      });
    } finally {
      setSendingMessage(false);
    }
  };

  const copyInquiryUrl = () => {
    navigator.clipboard.writeText(inquiryUrl);
    setNotification({
      show: true,
      type: 'info',
      message: translateText('URL Copied')
    });
  };

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)',
      padding: '0',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      direction: isArabic ? 'rtl' : 'ltr',
      position: 'relative',
      overflow: 'hidden',
      fontSize: '16px',
      lineHeight: '1.6',
      minHeight: '100vh'
    },
    languageToggle: {
      position: 'fixed',
      top: '100px',
      right: isArabic ? 'auto' : '20px',
      left: isArabic ? '20px' : 'auto',
      zIndex: '1000'
    },
    toggleButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '10px 20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    header: {
      textAlign: 'center',
      color: '#1a1a1a',
      padding: '120px 20px 60px',
      background: `linear-gradient(135deg, 
        rgba(220, 53, 69, 0.05) 0%, 
        rgba(220, 53, 69, 0.1) 50%, 
        rgba(255, 255, 255, 0) 100%)`,
      position: 'relative',
      overflow: 'hidden'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '1rem',
      background: 'linear-gradient(135deg, #dc3545 0%, #1a1a1a 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textShadow: '0 5px 15px rgba(220, 53, 69, 0.2)',
      letterSpacing: '-1px',
      lineHeight: '1.2'
    },
    subtitle: {
      fontSize: '1.1rem',
      marginBottom: '2rem',
      color: '#dc3545',
      fontWeight: '500',
      maxWidth: '600px',
      margin: '0 auto 2rem',
      lineHeight: '1.5'
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: '2rem',
      borderRadius: '20px',
      boxShadow: '0 15px 40px rgba(220, 53, 69, 0.08)',
      backdropFilter: 'blur(5px)',
      border: '1px solid rgba(220, 53, 69, 0.1)',
      marginBottom: '3rem'
    },
    inquiryUrlSection: {
      backgroundColor: 'rgba(220, 53, 69, 0.05)',
      padding: '1.5rem',
      borderRadius: '15px',
      border: '2px solid rgba(220, 53, 69, 0.1)',
      marginBottom: '2rem'
    },
    chatContainer: {
      border: '2px solid rgba(220, 53, 69, 0.1)',
      borderRadius: '15px',
      overflow: 'hidden',
      backgroundColor: '#ffffff'
    },
    messagesArea: {
      height: '400px',
      overflowY: 'auto',
      padding: '1.5rem',
      backgroundColor: '#fff5f5'
    },
    messageBubble: {
      padding: '1rem 1.5rem',
      borderRadius: '15px',
      marginBottom: '1rem',
      maxWidth: '80%',
      wordWrap: 'break-word'
    },
    inputArea: {
      padding: '1.5rem',
      borderTop: '2px solid rgba(220, 53, 69, 0.1)',
      backgroundColor: '#ffffff'
    },
    formControl: {
      backgroundColor: '#ffffff',
      border: '2px solid rgba(220, 53, 69, 0.1)',
      borderRadius: '10px',
      padding: '12px 15px',
      fontSize: '1rem',
      resize: 'none'
    },
    footer: {
      textAlign: 'center',
      marginTop: '3rem',
      padding: '2rem 1rem',
      color: '#1a1a1a',
      borderTop: '2px solid rgba(220, 53, 69, 0.1)',
      backgroundColor: 'rgba(255, 245, 245, 0.8)',
      fontWeight: '600',
      fontSize: '1rem'
    },
    floatingShapes: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      pointerEvents: 'none'
    },
    shape: {
      position: 'absolute',
      background: 'rgba(220, 53, 69, 0.1)',
      borderRadius: '50%'
    },
    urlPlaceholder: {
      backgroundColor: 'rgba(255, 193, 7, 0.1)',
      padding: '1.5rem',
      borderRadius: '15px',
      border: '2px solid rgba(255, 193, 7, 0.3)',
      marginBottom: '2rem',
      textAlign: 'center'
    }
  };

  return (
    <div style={styles.container}>
      {/* Floating Background Shapes */}
      <div style={styles.floatingShapes}>
        <div style={{...styles.shape, width: '150px', height: '150px', top: '10%', left: '5%', background: 'rgba(220, 53, 69, 0.05)'}}></div>
        <div style={{...styles.shape, width: '120px', height: '120px', top: '60%', right: '10%', background: 'rgba(220, 53, 69, 0.08)'}}></div>
        <div style={{...styles.shape, width: '80px', height: '80px', bottom: '20%', left: '15%', background: 'rgba(220, 53, 69, 0.03)'}}></div>
      </div>

      {/* Language Toggle Button */}
      <div style={styles.languageToggle}>
        <button 
          onClick={toggleLanguage}
          style={styles.toggleButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#1a1a1a';
            e.target.style.transform = 'translateY(-3px) scale(1.05)';
            e.target.style.boxShadow = '0 8px 20px rgba(26, 26, 26, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#dc3545';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.3)';
          }}
        >
          {isArabic ? 'ENGLISH' : 'العربية'}
        </button>
      </div>

      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.title}>{translateText('Contact Us')}</h1>
        <p style={styles.subtitle}>
          {translateText('Live Support')}
        </p>
      </header>

      {/* Main Content */}
      <div style={styles.content}>
        {notification.show && (
          <Alert 
            variant={notification.type} 
            style={{ 
              borderRadius: '10px',
              border: 'none',
              backgroundColor: notification.type === 'success' ? '#d4edda' : 
                             notification.type === 'danger' ? '#f8d7da' : 
                             notification.type === 'info' ? '#cce7ff' : '#fff3cd',
              color: notification.type === 'success' ? '#155724' : 
                    notification.type === 'danger' ? '#721c24' : 
                    notification.type === 'info' ? '#004085' : '#856404',
              marginBottom: '2rem'
            }}
            onClose={() => setNotification({ show: false })}
            dismissible
          >
            {notification.message}
          </Alert>
        )}

        {/* Inquiry URL Section - Only show after first message */}
        {firstMessageSent && inquiryUrl ? (
          <div style={styles.inquiryUrlSection}>
            <h5 style={{ color: '#dc3545', marginBottom: '1rem', fontWeight: '600' }}>
              📋 {translateText('Your Inquiry URL')}
            </h5>
            <InputGroup>
              <Form.Control
                value={inquiryUrl}
                readOnly
                style={{ 
                  backgroundColor: '#ffffff',
                  border: '2px solid rgba(220, 53, 69, 0.2)',
                  borderRadius: '8px',
                  padding: '12px 15px',
                  fontSize: '0.9rem'
                }}
              />
              <Button 
                variant="outline-primary"
                onClick={copyInquiryUrl}
                style={{
                  borderColor: '#dc3545',
                  color: '#dc3545',
                  borderRadius: '8px',
                  fontWeight: '600',
                  padding: '12px 20px'
                }}
              >
                {translateText('Copy URL')}
              </Button>
            </InputGroup>
            <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
              💡 {translateText('Share this URL with our support team for follow-up')}
            </small>
          </div>
        ) : (
          /* URL Placeholder - Show before first message */
          <div style={styles.urlPlaceholder}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔗</div>
            <h5 style={{ color: '#856404', marginBottom: '0.5rem' }}>
              {translateText('Your Inquiry URL')}
            </h5>
            <p style={{ color: '#856404', margin: 0 }}>
              {translateText('Start by sending your first message to get your inquiry URL')}
            </p>
          </div>
        )}

        {/* Chat Interface */}
        <div style={styles.chatContainer}>
          <div style={{ 
            backgroundColor: '#dc3545', 
            color: 'white',
            padding: '1.5rem',
            borderBottom: '2px solid rgba(220, 53, 69, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0, fontWeight: '600' }}>
                💬 {translateText('Live Support')}
              </h4>
              <Badge 
                bg="light" 
                text="dark"
                style={{ 
                  fontSize: '0.9rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontWeight: '600'
                }}
              >
                {chatLoading ? (
                  <>
                    <Spinner animation="border" size="sm" style={{ marginRight: '0.5rem' }} />
                    {translateText('Connecting to support...')}
                  </>
                ) : (
                  translateText('Chat Connected')
                )}
              </Badge>
            </div>
            <p style={{ margin: '0.5rem 0 0 0', opacity: '0.9' }}>
              {translateText('Start a conversation with our support team')}
            </p>
          </div>
          
          {/* Messages Area */}
          <div style={styles.messagesArea}>
            {conversation.length > 0 ? (
              conversation.map((msg, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.messageBubble,
                    backgroundColor: msg.sender === 'client' ? '#dc3545' : '#e9ecef',
                    color: msg.sender === 'client' ? 'white' : '#1a1a1a',
                    marginLeft: msg.sender === 'client' ? 'auto' : '0',
                    marginRight: msg.sender === 'client' ? '0' : 'auto',
                    border: msg.sender === 'client' ? 'none' : '1px solid rgba(220, 53, 69, 0.1)'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    {msg.sender === 'client' ? '👤 ' + translateText('You') : '🛠️ ' + translateText('Support Team')}
                  </div>
                  <div style={{ lineHeight: '1.5' }}>{msg.message}</div>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    opacity: '0.7',
                    marginTop: '0.5rem',
                    textAlign: msg.sender === 'client' ? 'right' : 'left'
                  }}>
                    {msg.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ 
                textAlign: 'center', 
                color: '#666',
                padding: '3rem 1rem',
                fontStyle: 'italic'
              }}>
                {translateText('No messages yet. Start the conversation!')}
              </div>
            )}
          </div>

          {/* Input Area */}
          <div style={styles.inputArea}>
            <Form onSubmit={handleSendMessage}>
              <Form.Group>
                <Form.Label style={{ fontWeight: '600', color: '#1a1a1a', marginBottom: '0.5rem' }}>
                  ✏️ {translateText('Your Message')}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={translateText('Type your message here...')}
                  disabled={chatLoading || sendingMessage}
                  style={styles.formControl}
                />
              </Form.Group>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <Button
                  type="submit"
                  disabled={chatLoading || sendingMessage || !newMessage.trim()}
                  style={{
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545',
                    borderRadius: '10px',
                    padding: '12px 30px',
                    fontWeight: '600',
                    fontSize: '1rem',
                    boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = '#1a1a1a';
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = '#dc3545';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {sendingMessage ? (
                    <>
                      <Spinner animation="border" size="sm" style={{ marginRight: '0.5rem' }} />
                      {translateText('Sending...')}
                    </>
                  ) : (
                    <>
                      📤 {translateText('Send')}
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={styles.footer}>
        <p>{translateText('Truth and credibility are our commitment')}</p>
      </footer>
    </div>
  );
};

export default Contact;
